import type { TrackPreset } from '../types/track';

const CIRCUIT_CACHE_KEY = 'tune-datalog-circuits';

export type CircuitCacheEntry = {
  isCircuit: boolean;
  lapCount: number;
  /** Matched track preset name (null if none). */
  trackName?: string | null;
  /** Compact signature of presets used for matching, for cache invalidation. */
  presetsSig?: string;
};

type CircuitCache = Record<string, CircuitCacheEntry>;

function readCache(): CircuitCache {
  try {
    const raw = sessionStorage.getItem(CIRCUIT_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as CircuitCache;
  } catch {
    return {};
  }
}

function writeCache(cache: CircuitCache) {
  try {
    sessionStorage.setItem(CIRCUIT_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage quota errors.
  }
}

export function computePresetsSignature(presets: TrackPreset[]): string {
  let hash = 2_166_136_261;

  for (const preset of presets) {
    const chunk = `${preset.id}:${preset.finish.lat},${preset.finish.lon}`;
    for (let index = 0; index < chunk.length; index += 1) {
      hash ^= chunk.charCodeAt(index);
      hash = Math.imul(hash, 1_677_761_9);
    }
  }

  return `${presets.length}:${(hash >>> 0).toString(36)}`;
}

export function shouldCacheCircuitEntry(entry: CircuitCacheEntry): boolean {
  return entry.isCircuit && entry.lapCount > 0;
}

/** Every circuit found this session, by datalog: read once for a list. */
export function getCachedDatalogCircuits(): Readonly<Record<string, CircuitCacheEntry>> {
  return readCache();
}

export function getCachedDatalogCircuit(id: string): CircuitCacheEntry | undefined {
  const cache = readCache();
  return cache[id];
}

export function setCachedDatalogCircuit(id: string, entry: CircuitCacheEntry) {
  if (!shouldCacheCircuitEntry(entry)) return;

  const cache = readCache();
  cache[id] = entry;
  writeCache(cache);
}

export function clearCachedDatalogCircuit(id: string) {
  const cache = readCache();
  delete cache[id];
  writeCache(cache);
}

/** Every circuit found, for when the owner signs out. */
export function forgetDatalogCircuits() {
  try {
    sessionStorage.removeItem(CIRCUIT_CACHE_KEY);
  } catch {
    // No storage: nothing was kept.
  }
}
